import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from '../api';
import { displayLocation } from '../location';
import { TierBadge, FLAG_WORDS } from '../products/StorageTierCard';

// One location (docs/STORAGE-TIER.md "Where it shows"): /warehouse/location/<code>. GET /api/storage/location wraps the
// floor-plan view (where the section stands, its picking stop, the walk from the P&P station, what is stored there)
// and the storage picture: the stack's boxes used / free, the styles on it with their tier and whether they fit, colour
// clashes. Opened from the floor plan's side panel and from a product page's shelf chips. Read-only. -- Claude, 2026-10-31

export default function LocationPage() {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    setErr(''); setD(null);
    authFetch(`${API}/storage/location?code=${encodeURIComponent(code)}`).then(async r => {
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : `HTTP ${r.status}`);
      setD(j);
    }).catch(e => setErr(e.message));
  }, [code]);
  if (err) return <div className="p-6"><button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white text-sm">← Back</button><div className="text-red-400 text-sm mt-2">{err}</div></div>;
  if (!d) return <div className="p-6 text-gray-500 text-sm">Loading…</div>;
  const L = d.location || {};
  const isUnit = L.kind === 'pallet' || L.kind === 'bay';
  const title = isUnit ? L.id : displayLocation(L.code || code);
  return (
    <div className="p-4 sm:p-6 max-w-5xl">
      <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white text-sm">← Back</button>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-1 mb-3">
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        <span className="text-gray-400 text-sm">
          {isUnit ? `${L.kind} · row ${L.row}${L.setup ? ` · ${L.setup}` : ''}` : L.kind === 'section' ? `face ${L.face} · row ${L.row || '—'} · aisle ${L.aisle || '—'}${L.box ? ` · ${L.box} stack` : ''}${L.label ? ` · "${L.label}"` : ''}` : L.meaning}
        </span>
      </div>
      {L.meaning && L.kind === 'section' && <div className="text-amber-300 text-sm mb-2">{L.meaning}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4">
        <div className="space-y-4">
          <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-4 text-sm">
            <div className="text-white font-semibold mb-1">On the floor plan</div>
            {L.kind === 'section' ? (
              <div className="text-gray-300 text-xs space-y-0.5">
                {L.picking_stop ? <div>Picking stop <b>{L.picking_stop.stop} of {L.picking_stop.of}</b> in {L.picking_stop.aisle} (left {L.picking_stop.left.map(displayLocation).join(' ')} | right {L.picking_stop.right.map(displayLocation).join(' ')})</div> : <div className="text-amber-300">In no picking stop.</div>}
                {L.walk_from_pp_station_m != null && <div>Walk from the P&amp;P station: <b>{L.walk_from_pp_station_m} m</b></div>}
                {L.unit && <div>On {L.unit.kind} <b>{L.unit.id}</b>{L.unit.setup ? ` (${L.unit.setup})` : ''}</div>}
                {L.from_top_cm != null && <div>{L.from_top_cm} cm from the top wall · {L.length_cm} cm long</div>}
                {L.stock_code && L.stock_code !== L.code && <div>Stock code <span className="font-mono">{L.stock_code}</span></div>}
              </div>
            ) : isUnit ? (
              <div className="text-gray-300 text-xs">{(L.codes || []).map((c: any) => <button key={c.code} onClick={() => navigate(`/warehouse/location/${encodeURIComponent(c.code)}`)} className="mr-2 underline hover:text-white">{displayLocation(c.code)}{c.box ? ` (${c.box})` : ''}</button>)}</div>
            ) : <div className="text-gray-400 text-xs">{L.meaning}</div>}
          </div>

          {(d.stacks || []).map((st: any) => (
            <div key={st.code} className="bg-[#1a1d27] rounded-xl border border-gray-800 p-4 text-sm">
              <div className="flex items-baseline justify-between">
                <div className="text-white font-semibold">Stack {displayLocation(st.code)} <span className="text-gray-500 font-normal text-xs">{st.box_type} · {st.height} boxes high{st.reachable ? ` · ${st.reachable} reachable` : ''}</span></div>
                <div className={`text-xs ${Number(st.boxes_free) < 0 ? 'text-amber-300' : 'text-gray-300'}`}>≈ {st.boxes_used} used · {st.boxes_free} free</div>
              </div>
              <div className="mt-2 h-2 rounded bg-gray-800 overflow-hidden"><div className={`h-2 ${Number(st.boxes_free) < 0 ? 'bg-amber-500' : 'bg-blue-600'}`} style={{ width: `${Math.min(100, Math.round(100 * Number(st.boxes_used) / Math.max(1, Number(st.height))))}%` }} /></div>
              {st.clash && <div className="text-amber-300 text-xs mt-2">Colour clash: {(st.clash_detail || []).map((c: any) => `${c.class ? c.class + ' ' : ''}${c.colour} × ${c.styles.length}`).join(' · ')}</div>}
              {st.off_home_styles > 0 && <div className="text-amber-300 text-xs mt-1">{st.off_home_styles} managed style{st.off_home_styles !== 1 ? 's' : ''} off its home face</div>}
            </div>
          ))}
          {!(d.stacks || []).length && L.kind === 'section' && <div className="text-gray-500 text-xs">No stack result for this section — it has no box type on the floor plan (Mark stacks), or the night has not run.</div>}
          <div className="text-gray-600 text-[11px]">{d.note}{d.as_of ? ` · as of ${String(d.as_of).slice(0, 16).replace('T', ' ')}` : ''}</div>
        </div>

        <div className="space-y-4">
          <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-4 text-sm">
            <div className="text-white font-semibold mb-2">Styles here ({(d.styles || []).length})</div>
            {(d.styles || []).map((s: any) => {
              const here = (d.stacks || []).flatMap((st: any) => (st.styles || []).filter((x: any) => x.style_id === s.style_id));
              const pcs = here.reduce((a: number, x: any) => a + Number(x.pieces || 0), 0), bx = here.reduce((a: number, x: any) => a + Number(x.boxes || 0), 0);
              return (
                <button key={s.style_id} onClick={() => s.wms_id && navigate(`/products/${s.wms_id}`)} className="w-full text-left py-1.5 border-t border-gray-800 first:border-0 hover:bg-gray-800/40 rounded px-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <TierBadge tier={s.tier} size="sm" />
                    <span className="text-white font-medium">{s.display_sku}</span><span className="text-gray-400 text-xs">{s.name}</span>
                    <span className="text-gray-500 text-xs">{[s.family, s.garment_class, s.colour].filter(Boolean).join(' · ')}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    here {pcs} pcs ≈ {bx} box{bx !== 1 ? 'es' : ''} · should {s.boxes_should ?? '—'} / has {s.boxes_has ?? '—'} in all
                    {s.fit === 'under' && <span className="text-amber-300"> · under room</span>}
                    {s.off_home && <span className="text-amber-300"> · off home face ({(s.home_faces || []).join(', ')})</span>}
                    {(s.flags || []).filter((f: string) => f === 'low_season' || f === 'colour_clash').map((f: string) => <span key={f} className="text-amber-300"> · {FLAG_WORDS[f]}</span>)}
                  </div>
                </button>
              );
            })}
            {!(d.styles || []).length && <div className="text-gray-500 text-xs">No style result here.</div>}
          </div>

          <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-4 text-sm">
            <div className="text-white font-semibold mb-2">Stored here <span className="text-gray-500 font-normal text-xs">{L.pieces} pcs · {L.products_total} products{L.truncated ? ' (first 200)' : ''}</span></div>
            <table className="w-full text-xs">
              <thead className="text-gray-500 text-left"><tr><th>Article</th><th>Name</th><th>Size</th><th className="text-right">Pcs</th><th>Shops</th></tr></thead>
              <tbody>{(L.products || []).map((p: any) => (
                <tr key={`${p.code}${p.barcode || p.article_number}${p.size}`} className="border-t border-gray-800 text-gray-300">
                  <td className="py-0.5 pr-2 font-mono">{p.article_number}</td><td className="py-0.5 pr-2">{p.name}</td><td className="py-0.5 pr-2">{p.size}</td>
                  <td className="py-0.5 pr-2 text-right">{p.pieces}</td><td className="py-0.5 text-gray-500">{Object.entries(p.by_store || {}).map(([k, v]) => `${k} ${v}`).join(' · ')}</td>
                </tr>
              ))}</tbody>
            </table>
            {L.count_rule && <div className="text-gray-600 text-[11px] mt-2">{L.count_rule}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
